import React, { memo } from "react";
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { formatAvgDaysLate, getOnTimeTone } from "../../utils/dashboardUtils";

const ON_TIME_FILL = "#bbf7d0";
const LATE_FILL = "#fecaca";
const BAR_SIZE = 33;
const BAR_RADIUS = 5;
const BAR_LABEL_FONT_SIZE = 12;

const EMPTY_FILL = "#e5e7eb";

const TatPersonBar = memo(({ row, onPersonClick, teamView }) => {
  const total = row.onTime + row.late;
  const isEmpty = total === 0;

  const chartData = isEmpty ? [{ person: row.person, empty: 1 }] : [row];

  const onTimeRadius = row.late === 0
    ? [BAR_RADIUS, BAR_RADIUS, BAR_RADIUS, BAR_RADIUS]
    : [BAR_RADIUS, 0, 0, BAR_RADIUS];
  const lateRadius = row.onTime === 0
    ? [BAR_RADIUS, BAR_RADIUS, BAR_RADIUS, BAR_RADIUS]
    : [0, BAR_RADIUS, BAR_RADIUS, 0];

  return (
    <div className="tat-person-row">
      <div className="tat-person-name-cell" style={{ minWidth: 0 }}>
        <button
          type="button"
          className="dashboard-person-link"
          onClick={() => {
            const personInfo = { ...row, id: row.user_id || row.id };
            if (teamView === "hod") {
              personInfo.isDepartment = true;
              personInfo.department = row.name || row.person;
            } else {
              personInfo.person = row.name || row.person;
            }
            onPersonClick?.(personInfo, true);
          }}
          title={row.name || row.person}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            display: "block",
            textAlign: "left"
          }}
        >
          {row.name || row.person}
        </button>
      </div>
      <div className="tat-person-bar-cell">
        <ResponsiveContainer width="100%" height={BAR_SIZE}>
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          >
            <XAxis type="number" domain={isEmpty ? [0, 1] : [0, total]} hide />
            <YAxis
              type="category"
              dataKey="person"
              width={0}
              tick={false}
              axisLine={false}
              tickLine={false}
            />
            {isEmpty ? (
              <Bar
                dataKey="empty"
                stackId="tat"
                fill={EMPTY_FILL}
                radius={[BAR_RADIUS, BAR_RADIUS, BAR_RADIUS, BAR_RADIUS]}
                barSize={BAR_SIZE}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="empty"
                  position="center"
                  fill="#9ca3af"
                  fontSize={BAR_LABEL_FONT_SIZE}
                  fontWeight={500}
                  formatter={() => "No tasks"}
                />
              </Bar>
            ) : (
              <>
                <Bar
                  dataKey="onTime"
                  stackId="tat"
                  fill={ON_TIME_FILL}
                  radius={onTimeRadius}
                  barSize={BAR_SIZE}
                  isAnimationActive={false}
                >
                  {row.onTime > 0 && (
                    <LabelList
                      dataKey="onTime"
                      position="center"
                      fill="#166534"
                      fontSize={BAR_LABEL_FONT_SIZE}
                      fontWeight={600}
                      formatter={(value) => `${value} on time`}
                    />
                  )}
                </Bar>
                <Bar
                  dataKey="late"
                  stackId="tat"
                  fill={LATE_FILL}
                  radius={lateRadius}
                  barSize={BAR_SIZE}
                  isAnimationActive={false}
                >
                  {row.late > 0 && (
                    <LabelList
                      dataKey="late"
                      position="center"
                      fill="#991b1b"
                      fontSize={BAR_LABEL_FONT_SIZE}
                      fontWeight={600}
                      formatter={(value) => `${value} late`}
                    />
                  )}
                </Bar>
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div 
        className="tat-person-pct-cell" 
        style={{ 
          color: isEmpty 
            ? "#9ca3af"
            : getOnTimeTone(row.onTimePct) === "success" 
              ? "#166534" 
              : getOnTimeTone(row.onTimePct) === "warning" 
                ? "#b45309" 
                : "#991b1b" 
        }}
      >
        {row.onTimePct}%
      </div>
      <div className="tat-person-avg-cell">{formatAvgDaysLate(row.avgDaysLate)}</div>
    </div>
  );
});

TatPersonBar.displayName = "TatPersonBar";

const TatByPersonChart = ({
  rows = [],
  isLoading = false,
  totalItems = 0,
  currentPage = 1,
  pageSize = 10,
  onPaginationChange,
  onPersonClick,
  teamView,
}) => {
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const showPagination = totalItems > pageSize || currentPage > 1;

  if (!rows.length && !isLoading) {
    return <p className="dashboard-section-empty">No TAT data available.</p>;
  }

  return (
    <section className="dashboard-section">
      <h3 className="dashboard-section-title">
        {teamView === "hod" ? "TAT by Department" : "TAT by Person"} — Tasks Closed Within Due Date
      </h3>
      <div className="tat-by-person-chart">
        {rows.map((row) => (
          <TatPersonBar key={row.id || row.user_id} row={row} onPersonClick={onPersonClick} teamView={teamView} />
        ))}
      </div>

      {showPagination && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            padding: "8px 0 4px",
            fontSize: "0.8rem",
            color: "#6b7280",
          }}
        >
          <span>
            Page {currentPage} of {totalPages}
            {totalItems > 0 && ` (${totalItems} total)`}
          </span>
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => onPaginationChange?.({ page: currentPage - 1 })}
            style={{
              padding: "3px 10px",
              borderRadius: 6,
              border: "1px solid #d1d5db",
              background: currentPage <= 1 ? "#f3f4f6" : "#fff",
              color: currentPage <= 1 ? "#9ca3af" : "#374151",
              cursor: currentPage <= 1 ? "not-allowed" : "pointer",
              fontSize: "0.8rem",
            }}
          >
            ← Prev
          </button>
          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => onPaginationChange?.({ page: currentPage + 1 })}
            style={{
              padding: "3px 10px",
              borderRadius: 6,
              border: "1px solid #d1d5db",
              background: currentPage >= totalPages ? "#f3f4f6" : "#fff",
              color: currentPage >= totalPages ? "#9ca3af" : "#374151",
              cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
              fontSize: "0.8rem",
            }}
          >
            Next →
          </button>
        </div>
      )}
    </section>
  );
};

export default memo(TatByPersonChart);

